'use client';
import React from 'react';
import { DiscountCode as Shared } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { useCart } from '@/lib/hooks/useCart';
import { convertToLocale } from '@lib/util/money';

const SharedRender = (Shared as any).render;

/**
 * Storefront DiscountCode — derives `appliedDiscount` from the cart's
 * first applied discount, and wires `onApply` / `onRemove` to the real
 * Medusa cart-promotions endpoints via useCart(). Falls back to the
 * shared MOCK only in editor preview.
 */
function DiscountCodeImpl(props: any) {
  const { context } = usePuckContext() as any;
  const { applyDiscount, refreshCart } = useCart() as any;
  const cart = context?.cart;

  if (!cart) {
    return <SharedRender {...props} />;
  }

  const currency = cart.currency_code || 'usd';
  const formatPrice = (price: number) =>
    convertToLocale({ amount: Number(price) || 0, currency_code: currency });

  const first = Array.isArray(cart.discounts) && cart.discounts.length > 0 ? cart.discounts[0] : null;
  const appliedDiscount = first
    ? {
        code: first.code ?? '',
        amount: Number(first.amount ?? cart.discount_total ?? 0),
      }
    : null;

  const onApply = async (code: string) => {
    await applyDiscount(code);
    await refreshCart();
  };
  const onRemove = async () => {
    if (!first?.code) return;
    try {
      await fetch(`/api/cart/promotions`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codes: [first.code] }),
      });
    } catch {
      // ignore — useCart will refetch on next add
    }
    await refreshCart();
  };

  return (
    <SharedRender
      {...props}
      appliedDiscount={appliedDiscount}
      onApply={onApply}
      onRemove={onRemove}
      formatPrice={formatPrice}
    />
  );
}

export const DiscountCode: any = {
  ...Shared,
  render: (props: any) => <DiscountCodeImpl {...props} />,
};
export default DiscountCode;
